// ./src/App.tsx
import React, { Suspense, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RouteFallback } from "@/components/generic/RouteFallback";
import { SdFlcReactRouterPages } from "@sdflc/react-router-pages";
import "@/i18n";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/tiptap/styles.css";
import '@/styles/global.css';

import {
  AppProvider,
  useApp,
  GoogleMapsProvider,
  ThemeProvider,
  AccountPreferenceProvider,
  EventSetupCatalogProvider,
  EmNotificationsProvider,
  OrganizerProfilesProvider,
  TagsProvider,
  CategoriesProvider,
  MembershipTypesProvider,
  PageVisibilityProvider,
  EntityFiltersProvider,
  PWAProvider,
  LayoutUIProvider,
  PageChromeProvider,
  AppEnvironmentProvider,
  LocationHistoryProvider,
  BrandingProvider,
  useBranding,
  CurrentEventProvider,
  CrewAccessProvider,
} from "@/contexts";
import { LoadingOverlay, Favicon } from "@/components/generic";
import { AppShellLayout } from "@/components/layouts";
import { setAuthErrorHandler, clearAuthErrorHandler } from "@/utils/api/authErrorHandler";
import { refreshSessionOnce, sessionNeedsRenewal, setSessionRefresher } from "@em/api";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { CrewSessionService } from "@/utils/api/CrewSessionService";
import { AuthService } from "@/utils/api/AuthService";
import { WHOLE_SET } from "@/utils/api/listAllPages";
import { DEFAULT_PAGE_SIZE, SIGNIN_PAGE } from "@/config";

import { usePageTracking } from "./hooks";
import { OnboardingGuard } from "./components/guards";
import { ErrorReportProvider } from "./components/features/ErrorReport";
import { CommandPalette } from "./components/features/CommandPalette";
import { AnalyticsConsentBanner } from "./components/features/AnalyticsConsent";
import { useWriteTimeoutNotice } from "@/hooks";

// ⓘ Google Analytics is initialised ONCE, by `utils/analytics` (ROADMAP #62 P7) — not here, not in index.html.

// ============================================
// Auth error handler setup hook
// ============================================

/**
 * Sets up the global auth error handler.
 * Must be called inside router context (needs useNavigate).
 */
const useAuthErrorSetup = () => {
  const navigate = useNavigate();
  const { signOut } = useApp();

  useEffect(() => {
    setAuthErrorHandler(async (code, message) => {
      console.warn(`[Auth Error] Code: ${code}, Message: ${message}`);

      // A NON-account crew session (docs/CREW-ACCESS.md) legitimately has no account
      // login, so account-scoped provider queries WILL 401 — that must not sign the
      // crew member out or bounce them to account sign-in. Swallow the auth error;
      // the crew UI surfaces access failures in-page. Also skip while on the crew
      // sign-in page itself (before a token exists).
      if (
        CrewSessionService.hasSession() ||
        window.location.pathname.startsWith("/crew-access")
      ) {
        return;
      }

      // ⚠ VERIFY THE SESSION BEFORE DESTROYING IT.
      //
      // ms_auth answers HTTP 200 with an operation-level OpResult and uses ONE code (-30100
      // UNAUTHORIZED) for two completely different things: "your session is gone" and "you are
      // signed in but not allowed to do that". Acting on the code alone meant an ordinary refusal
      // silently logged the user out — inviting a teammate signed the owner out, bounced them to
      // /account/signin and threw the invitation away, with the real reason never shown.
      //
      // `me()` is the only thing that can tell the two apart. If it still succeeds the session is
      // fine and this was a refusal: leave the user where they are and let the caller surface the
      // error (every drawer already renders `lastError`). Only a failing `me()` is a real logout.
      //
      // Costs one extra request, and ONLY on the error path. Recursion is not a risk: `me()`
      // returning UNAUTHORIZED re-enters triggerAuthError, which is already latched by
      // `isHandlingError` and returns immediately.
      const sessionCheck = await AuthService.me();
      if (sessionCheck.didSucceedAndHasData()) {
        console.warn(
          "[Auth Error] Session is still valid — treating this as a permission refusal, not a logout."
        );

        return;
      }

      // Clear auth state
      await signOut();

      // Build redirect URL with return path
      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams();

      // Only add returnUrl if not already on auth pages
      if (currentPath && !currentPath.startsWith("/account/")) {
        searchParams.set("returnUrl", currentPath);
      }

      const redirectUrl = searchParams.toString()
        ? `${SIGNIN_PAGE}?${searchParams.toString()}`
        : SIGNIN_PAGE;

      // Navigate to sign-in
      navigate(redirectUrl, { replace: true });
    });

    // Cleanup on unmount
    return () => {
      clearAuthErrorHandler();
    };
  }, [navigate, signOut]);
};

// ============================================
// Session refresh (ROADMAP #239)
// ============================================

/**
 * ROADMAP #239 (owner: "do refreshing when needed, but do not sign out user until session expired") — the access token is
 * short; the session it renews is not. @em/api renews it (once, shared by every call meanwhile, one tab at a time) BEFORE a
 * call whose token is known to be near expiry and AFTER a call refused for it, then repeats the call; this says HOW.
 * ⚠⚠ REGISTERED WHEN THIS MODULE LOADS, not in a component: the boot `me()` (AppContext) runs behind the "Initializing…"
 *   overlay, before any router-level component mounts — registered there, a reload after the token expired found no way
 *   to renew and signed the user out (review 2026-10-30).
 * ⚠ It declines only when a NON-account crew session is the identity in use (its own token; nothing to renew) — an
 *   account login takes precedence over a leftover crew token, exactly as AppContext decides.
 */
const refreshAppSession = async (): Promise<boolean> => {
  if (!AuthService.getStoredUserData() && CrewSessionService.hasSession()) {
    return false;
  }

  const result = await AuthService.refreshToken();

  return result.code === OP_RESULT_CODES.OK;
};

setSessionRefresher(refreshAppSession);

/**
 * …and renew AHEAD when a tab comes back from the background (a phone resumed after an hour), so its first call does not
 * meet an expired token at all. ⚠ Nothing runs while the tab is hidden: one check on `visibilitychange` / `pageshow`.
 */
const useSessionRefreshSetup = () => {
  useEffect(() => {
    const refreshAheadIfDue = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      if (sessionNeedsRenewal()) {
        void refreshSessionOnce(Date.now());
      }
    };

    document.addEventListener("visibilitychange", refreshAheadIfDue);
    window.addEventListener("pageshow", refreshAheadIfDue);

    return () => {
      document.removeEventListener("visibilitychange", refreshAheadIfDue);
      window.removeEventListener("pageshow", refreshAheadIfDue);
    };
  }, []);
};

// ============================================
// Public auth pages - no providers needed
// ============================================

const PUBLIC_AUTH_PAGES = [
  "/account/signin",
  "/account/signup",
  "/account/forgot-password",
  "/account/reset-password",
  "/account/accept-invitation",
  // Crew sign-in (non-account) — public, renders without the account providers.
  "/crew-access",
];

const isPublicAuthPage = (): boolean => {
  const pathname = window.location.pathname;
  return PUBLIC_AUTH_PAGES.some(
    (route) => pathname === route || pathname.startsWith(route + "/")
  );
};

// ============================================
// Router Wrapper for public pages (minimal)
// ============================================

const PublicRouterWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // ⚠ See the note on the authenticated wrapper's boundary — every routed page is `React.lazy`,
  // and until #51 nothing in this app caught the suspension.
  // ⚠ AppEnvironmentProvider here too (2026-10-14): `SelectField` reads it (phone → full-screen picker) and THROWS
  //   without it. The sign-up and accept-invitation forms gained pickers (account type, month/year of birth) and blanked
  //   the page. It is media queries only — no account data — so it is safe before sign-in.
  return (
    <AppEnvironmentProvider>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </AppEnvironmentProvider>
  );
};

// ============================================
// Router Wrapper for authenticated pages (full)
// Includes all providers that need router context
// ============================================

const AuthenticatedRouterWrapper: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Set up global auth error handler
  useAuthErrorSetup();
  useSessionRefreshSetup();
  // ROADMAP #131 — say so when the lists re-read after a save that got no answer.
  useWriteTimeoutNotice();

  return (
    <PageVisibilityProvider>
      <AppEnvironmentProvider>
        <PWAProvider
          updateCheckInterval={60 * 60 * 1000}
          autoUpdate={false}
        >

          <EntityFiltersProvider>
            {/* The account's EVENT DEFAULTS (A2b #53) — read by the Event Defaults page AND by
                the Create Event wizard, which is reachable from anywhere, so this has to sit above
                the routes. It takes the slot of the old user-preferences provider, which polled a
                query no eventsmagic subgraph has ever defined (it belongs to carexpenses-api). */}
            <AccountPreferenceProvider>
              {/* A2b #62 — the EVENT SETUP CATALOG (types, synonyms, offers, rules). App-wide because the event card,
                  the dashboard header, Home, Details & Settings and the wizard all name an event's type from it —
                  and it replaced a hand-kept FE copy that had drifted from the seed. One small read, never polled. */}
              <EventSetupCatalogProvider>
              {/* The notification BELL lives in the shell, so its badge has to be readable from
                  above the routes (A2b #57).
                  ⚠⚠ autoLoad fetches the COUNT ONLY, once, and NOTHING POLLS. The provider this
                  replaces in spirit — the deleted carexpenses bell — polled a foreign query every
                  60 seconds for every signed-in user and nothing ever failed loudly, because a
                  query the gateway rejects looks exactly like an empty inbox. The LIST is fetched
                  only when somebody actually opens the bell or the page. */}
              <EmNotificationsProvider pageSize={DEFAULT_PAGE_SIZE} autoLoad liveBadge>
                {/* TagsProvider loads the account's tags as a shared read cache. */}
                <TagsProvider pageSize={WHOLE_SET}>
                  {/* Categories are the shared vocabulary behind every category
                      picker (tasks, timeline, POIs, map, budget…) — like tags,
                      account-global, so they're loaded once here and a category
                      created in place anywhere is immediately offered everywhere.
                      Read WHOLE (WHOLE_SET, no cap): the picker needs every
                      category (the system catalog alone is ~60 rows), and a cap
                      silently dropped the rest (ROADMAP #118). */}
                  <CategoriesProvider pageSize={WHOLE_SET}>
                  {/* Membership types are a shared lookup (the Memberships page's picker, filter and
                      names), so the provider lives here — fetched once, WHOLE (#118: it read 50). The
                      Membership Types page pages through its OWN provider and re-reads this one on leaving. */}
                  <MembershipTypesProvider pageSize={WHOLE_SET}>
                  {/* A2b #54 — the account's ORGANIZER PAGES. App-wide because the thing that
                      makes them useful is the PICKER, and that lives on Details & Settings and in
                      the create-event wizard, which is reachable from anywhere. A provider on the
                      Library page would leave both with nothing to pick from. Read WHOLE (#118); the
                      Library page pages through its OWN provider and re-reads this one on leaving. */}
                  <OrganizerProfilesProvider pageSize={WHOLE_SET}>
                    <LayoutUIProvider>
                      {/* Tracks the event whose workspace we're in (from the URL)
                          so the shell + ⌘K palette know the current event. */}
                      <CurrentEventProvider>
                        {/* Resolves the caller's access to the current event so the
                            shell can run in "crew mode" (filtered nav) for a crew
                            member on someone else's event. Owner → full access. */}
                        <CrewAccessProvider>
                          {/* One feedback drawer for the whole app; any ErrorAlert
                              can open it pre-filled via useOptionalErrorReport. */}
                          <ErrorReportProvider>
                            {/* ⚠⚠ ROADMAP A2b #58 — the channel a PAGE uses to tell the app
                                SHELL what belongs in its header. The shell is now rendered above
                                the route (`AppShellLayout`), so a page can no longer hand it
                                props; it publishes them here instead and the shell subscribes.
                                ⚠ It sits ABOVE the routes on purpose — it is read by the layout,
                                which is itself above every page — and its value is a STABLE
                                store rather than state, because provider state up here would
                                re-render every page on every header update, and a page
                                republishes its header on each of its own renders. */}
                            <PageChromeProvider>
                            {/* ⚠⚠ THE APP'S ONLY ROUTE-LEVEL SUSPENSE BOUNDARY, and until
                                ROADMAP A2b #51 there was none at all. Every page in `siteMap.ts`
                                is `React.lazy`, so a first visit suspends; with no boundary React
                                fell back to the root and the screen went blank until the chunk
                                landed. `withPlanGate` carried its own boundary, so PLAN-GATED
                                pages behaved differently from every other page — which is part of
                                why some transitions looked fine and others flashed.
                                ⚠ It sits INSIDE every provider deliberately: a boundary above them
                                would let a chunk download unmount the shared caches (tags,
                                categories, the current event) and refetch them on every first
                                visit. See `RouteFallback` for why the loader is delayed. */}
                            <Suspense fallback={<RouteFallback />}>
                              <OnboardingGuard>{children}</OnboardingGuard>
                            </Suspense>
                            {/* Global ⌘K command palette (navigation + page commands). */}
                            <CommandPalette />
                            </PageChromeProvider>
                          </ErrorReportProvider>
                        </CrewAccessProvider>
                      </CurrentEventProvider>
                    </LayoutUIProvider>
                  </OrganizerProfilesProvider>
                  </MembershipTypesProvider>
                  </CategoriesProvider>
                </TagsProvider>
              </EmNotificationsProvider>
              </EventSetupCatalogProvider>
            </AccountPreferenceProvider>
          </EntityFiltersProvider>
        </PWAProvider>
      </AppEnvironmentProvider>
    </PageVisibilityProvider>
  );
};

// ============================================
// App Content for public pages
// ============================================

const PublicAppContent: React.FC = () => {
  const { siteMap, userRoles } = useApp();

  return (
    <SdFlcReactRouterPages
      siteMap={siteMap}
      roles={userRoles}
      appWrapper={PublicRouterWrapper}
    />
  );
};

// ============================================
// App Content for authenticated pages
// ============================================

const AuthenticatedAppContent: React.FC = () => {
  const { siteMap, userRoles } = useApp();

  return (
    <SdFlcReactRouterPages
      siteMap={siteMap}
      roles={userRoles}
      /**
       * ⚠⚠ ROADMAP A2b #58 — THIS ONE PROP IS THE FIX. The library renders
       * `<Layout><Page/></Layout>` for every route, so naming ONE layout here makes the shell the
       * same React component TYPE on every route: React reconciles it instead of throwing it away.
       * Until this line, `siteMap.ts` declared no layout anywhere and all 88 page surfaces rendered
       * `DashboardLayout` themselves — so two pages were two different types and every cross-page
       * navigation destroyed the sidebar, its scroll position, the nav tree and the bell.
       * ⚠ Pages that bring their own full-screen shell (print sheets, the wizard, onboarding, the
       * public and error screens) override it with `BareLayout` — `BARE_SHELL_COMPONENTS` in
       * `siteMap.ts` is the one list, applied over the assembled tree.
       * ⓘ NOT passed to `PublicAppContent`: the shell reads the account providers, and the public
       * router is mounted precisely when those are not there.
       */
      layout={AppShellLayout}
      appWrapper={AuthenticatedRouterWrapper}
    />
  );
};

// ============================================
// Main App with conditional provider loading
// ============================================

const AuthenticatedApp: React.FC = () => {
  const { isInitializing } = useApp();

  usePageTracking();

  // Show loading while initializing
  if (isInitializing) {
    return <LoadingOverlay visible loadingText="Initializing..." />;
  }

  // Public auth pages - skip all providers
  if (isPublicAuthPage()) {
    return <PublicAppContent />;
  }

  // Authenticated pages - providers are inside AuthenticatedRouterWrapper
  return <AuthenticatedAppContent />;
};

/**
 * A2e #101 — a FIRST visit to this host knows no brand yet: show the loader (the generic icon) until the white label is
 * read, so the app never opens under no name and then renames itself. A returning visit is ready at once (the brand is
 * remembered per host), and a read that cannot answer in time opens the app anyway (BrandingContext).
 */
const BrandReady: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ready } = useBranding();
  if (!ready) {
    return <LoadingOverlay visible loadingText="Initializing..." />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <BrandingProvider>
        <BrandReady>
          <Favicon />
          {/* #62 P7 — the one-time analytics question; at the root so it reaches the public sign-up page too. */}
          <AnalyticsConsentBanner />

          <AppProvider>
            <GoogleMapsProvider>
              <LocationHistoryProvider>
                <AuthenticatedApp />
              </LocationHistoryProvider>
            </GoogleMapsProvider>
          </AppProvider>
        </BrandReady>
      </BrandingProvider>
    </ThemeProvider>
  );
}

export default App;